/**
 * Dev-sync J1 — the psychrometric chart-data contract + fetch.
 *
 * Endpoint: GET /api/v1.0/ahu/units/<pk>/psychrometric_chart_data/
 * (PsychrometricChartViewSet.chart_data, api/rest_ahu/psychrometric_views.py
 * :2230, routed urls.py:124-126). Payload shapes below are the LIVE measured
 * response (unit 3512, survey §A1 / scratchpad syncJdiscover/chart_3512.json),
 * not guesses.
 *
 * ★★ WHY THIS IS A PLAIN fetch() AND NOT AN RTK QUERY ENDPOINT
 * (survey §A1 contract-gotcha 6): **this GET WRITES to the database.** When
 * `synchronize_air_conditions` is on, genuinely-blank (`None`) section
 * `eadb_temp`/`eawb_temp` values are backfilled via a bulk `.update()`
 * (psychrometric_views.py:2244-2257, `_persist_fills`:255). It is not
 * idempotent and must never sit behind cache machinery that can refetch on
 * its own (mount/arg-change/focus/reconnect re-runs, cache-expiry re-runs).
 * Legacy fetched at exactly two moments — dialog open (show) and explicit
 * refresh (restore / sync-toggle) — psychrometric_chart.js:527-562. A plain
 * promise-per-call reproduces that exactly-when-called contract with zero
 * cache surface. Do NOT convert this to `injectEndpoints` without pinning
 * every auto-refetch path off.
 *
 * Contract gotchas carried from the survey (§A1) that consumers must honor:
 *  - `tunnels` are AIR-STREAM PATHS (supply/return/exhaust/no_air_stream),
 *    not AHU tunnels (`_walk_unit`, psychrometric_views.py:1539).
 *  - `rh` is a FRACTION 0..1 everywhere, never a percentage.
 *  - `row_seasons` / `stale_section_ids[season]` serialize Python sets —
 *    array order is NON-DETERMINISTIC. Treat as sets / index by section_id.
 *  - Row identity is (component_kind, component_id): an EW/PHX bridge appears
 *    on both supply and exhaust paths with the SAME component_id but
 *    DIFFERENT section_ids (psychrometric_views.py:1852-1861).
 *  - Sidebar condition blocks carry only t_db/t_wb (no w/rh/h).
 */

import { getCookie } from 'helpers/index';

export type PsySeason = 'summer' | 'winter';
/** The chart's season <select> vocabulary — 'both' is client-side only. */
export type PsySeasonFilter = 'both' | PsySeason;

export interface PsyCurvePoint {
  t_db: number;
  /** humidity ratio in lb/lb dry air (the DISPLAY unit is gr/lb — ×7000). */
  w: number;
}

/** One air-condition state (entering/leaving of a chart entry). */
export interface PsyConditions {
  t_db: number | null;
  t_wb: number | null;
  w: number | null;
  /** FRACTION 0..1 (survey gotcha 2). */
  rh: number | null;
  h: number | null;
  /** data error on THIS point, entering or leaving — a WB above its own DB,
   * an RH over 100%, or a negative W floored to the zero line. Drawn
   * gray/open. When it marks the floor, `w` and `h` are the floored chart
   * substitutes rather than the readings the inputs work out to. */
  clamped?: boolean;
}

export interface PsyTunnelEntry {
  component_id: number;
  component_kind: string | null;
  section_id: number | null;
  component_short: string;
  component_long: string;
  component_label: string;
  entering: PsyConditions | null;
  leaving: PsyConditions | null;
  /** The process line from entering to leaving when it is NOT the straight
   * segment between them — a dehumidifying cooling coil, which runs at
   * constant humidity ratio to the entering dew point, down the saturation
   * curve to the leaving wet bulb, then across to the leaving point.
   * Includes both end points. Absent/null means draw the straight line. */
  process_path?: PsyCurvePoint[] | null;
  rated: boolean;
  order_index: number;
  /** 'Supply' | 'Return' | 'Exhaust' | 'No Air Stream' (line-dash encoding). */
  air_stream_label: string | null;
  /** serialized frozenset — NON-DETERMINISTIC order; treat as a set. */
  row_seasons: string[];
}

/** An air-stream path (NOT an AHU tunnel — survey gotcha 1). */
export interface PsyTunnelPath {
  name: string;
  path: 'supply' | 'return' | 'exhaust' | 'no_air_stream' | string;
  season_states: Partial<Record<PsySeason, PsyTunnelEntry[]>>;
}

export interface PsySidebarConditionPair {
  t_db: number | null;
  t_wb: number | null;
}

export interface PsySidebarCondition {
  entering: PsySidebarConditionPair;
  leaving: PsySidebarConditionPair;
  stale: boolean;
  /** derived server-side: is_stale AND kind in RERATE_KINDS. */
  needs_rerate: boolean;
}

export interface PsySidebarRow {
  section_id: number;
  component_id: number;
  tag: string | null;
  component_kind: string | null;
  component_long: string | null;
  component_short: string | null;
  /** SORTED server-side (psychrometric_views.py:2112) — safe to read. */
  seasons: PsySeason[];
  summer: PsySidebarCondition;
  winter: PsySidebarCondition;
}

export interface PsySidebarPath {
  name: string;
  path: string;
  rows: PsySidebarRow[];
}

/**
 * One stale_section_ids entry — at least {section_id}; carries
 * target/component_id/fields when a fresh entering condition is behind it
 * (consumed by the J5 re-rate handoff / SYNCHRONIZE payloads).
 */
export interface PsyStaleEntry {
  section_id: number;
  target?: 'section' | 'component';
  component_id?: number;
  fields?: Record<string, number>;
}

export interface PsyAnnotation {
  /** number once persisted; legacy uses "tmp-<ts>" strings pre-POST (J2). */
  id: number | string;
  label: string;
  t_db: number;
  /** lb/lb (display converts to gr/lb). */
  w: number;
  t_wb?: number | null;
  rh?: number | null;
  h?: number | null;
}

export interface PsyAxes {
  t_db_min: number;
  t_db_max: number;
  w_min: number;
  w_max: number;
}

export interface PsyChartData {
  elevation_ft: number;
  atmospheric_pressure_psia: number;
  synchronize_air_conditions: boolean;
  ahu_tag: string | null;
  project_name: string | null;
  curves: {
    saturation: PsyCurvePoint[];
    rh_lines: { rh: number; points: PsyCurvePoint[] }[];
    enthalpy_lines: { h: number; points: PsyCurvePoint[] }[];
    wet_bulb_lines: { t_wb: number; points: PsyCurvePoint[] }[];
    /** The wet-bulb scale: where each labelled wet bulb meets the
     * saturation curve, so `t_db === t_wb`. Optional — a cached response
     * from before the scale existed has no such key. */
    wet_bulb_labels?: { t_wb: number; t_db: number; w: number }[];
  };
  axes: PsyAxes;
  tunnels: PsyTunnelPath[];
  sidebar_paths: PsySidebarPath[];
  annotations: PsyAnnotation[];
  /** serialized Python sets — order non-deterministic; index by section_id. */
  stale_section_ids: Partial<Record<PsySeason, PsyStaleEntry[]>>;
}

/** stale_section_ids re-indexed by section_id, per season (renderChart
 * js:659-678 `staleBySeason`) — the shape traces/sidebar actually consume. */
export type PsyStaleBySeason = Record<PsySeason, Record<number, PsyStaleEntry>>;

export function computeStaleBySeason(data: PsyChartData | null): PsyStaleBySeason {
  const out: PsyStaleBySeason = { summer: {}, winter: {} };
  const raw = data?.stale_section_ids ?? {};
  (['summer', 'winter'] as const).forEach((season) => {
    (raw[season] ?? []).forEach((entry) => {
      out[season][entry.section_id] = entry;
    });
  });
  return out;
}

/**
 * The one fetch (legacy fetchChartData, psychrometric_chart.js:527-539).
 * Elevation is the project's own value — always fetched fresh, never sent
 * from the client (the `?elevation=` param exists but the UI's elevation
 * input is disabled; survey gotcha 7).
 */
export async function fetchChartData(ahuId: number): Promise<PsyChartData> {
  const resp = await fetch(`/api/v1.0/ahu/units/${ahuId}/psychrometric_chart_data/`, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
  if (!resp.ok) throw new Error(resp.statusText || 'request failed');
  return (await resp.json()) as PsyChartData;
}

// ---------------------------------------------------------------------------
// Dev-sync J2 — annotation CRUD (psychrometric_views.py:2340-2400).
//
// Routes (urls.py:127-135):
//   POST | DELETE  /api/v1.0/ahu/units/<pk>/psychrometric_annotations/
//   PATCH | DELETE /api/v1.0/ahu/units/<pk>/psychrometric_annotations/<id>
// ★ the DETAIL route has NO trailing slash (survey §D5 note) — legacy builds
// it as `url + ann.id` where the list url ends in '/' (js:2372).
//
// POST body {label, t_db, w} (all required, label[:80]); PATCH reads ONLY
// `label` (`str(label)[:80]`, no serializer validation). Both take
// `?elevation=<float>` so the response's derived t_wb/rh/h match the
// elevation the chart is showing (js:2349-2356) — legacy sends it on
// POST/PATCH and NOT on the deletes. Deletes return 204.
//
// None of these touch chart_data — legacy patches this.data.annotations
// locally and re-renders; the ONLY chart_data fetches remain show()/restore()
// (the write-on-GET rule, §A1 gotcha 6).
// ---------------------------------------------------------------------------

function csrfHeaders(): Record<string, string> {
  // Same cookie legacy's own csrfToken() read (js:77-82); shared app helper.
  const token = getCookie('csrftoken');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(token ? { 'X-CSRFToken': token } : {}),
  };
}

function annotationsUrl(ahuId: number): string {
  return `/api/v1.0/ahu/units/${ahuId}/psychrometric_annotations/`;
}

export interface PsyAnnotationBody {
  label: string;
  t_db: number;
  /** lb/lb — pixelToData already divided by GRAINS_PER_LB (js:2190-2199). */
  w: number;
}

/** POST — returns the saved row incl. server-derived id/t_wb/rh/h (201). */
export async function createAnnotation(
  ahuId: number,
  elevation: number,
  body: PsyAnnotationBody
): Promise<PsyAnnotation> {
  const resp = await fetch(
    `${annotationsUrl(ahuId)}?elevation=${encodeURIComponent(elevation)}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: csrfHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!resp.ok) throw new Error(resp.statusText || 'request failed');
  return (await resp.json()) as PsyAnnotation;
}

/** PATCH label — detail route WITHOUT trailing slash (js:2370-2377). */
export async function patchAnnotationLabel(
  ahuId: number,
  annotationId: number | string,
  elevation: number,
  label: string
): Promise<PsyAnnotation> {
  const resp = await fetch(
    `${annotationsUrl(ahuId)}${annotationId}?elevation=${encodeURIComponent(elevation)}`,
    {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: csrfHeaders(),
      body: JSON.stringify({ label }),
    }
  );
  if (!resp.ok) throw new Error(resp.statusText || 'request failed');
  return (await resp.json()) as PsyAnnotation;
}

/** DELETE one — detail route WITHOUT trailing slash, no elevation param
 * (js:2392-2397). Legacy fire-and-forgets with no .fail — errors swallowed. */
export async function deleteAnnotation(ahuId: number, annotationId: number): Promise<void> {
  await fetch(`${annotationsUrl(ahuId)}${annotationId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: csrfHeaders(),
  });
}

/** DELETE all (clear_annotations, views:2394-2400) — the LIST route (js:648-651);
 * this one DOES reject on failure (legacy .fail rolls the optimistic clear back). */
export async function clearAllAnnotations(ahuId: number): Promise<void> {
  const resp = await fetch(annotationsUrl(ahuId), {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: csrfHeaders(),
  });
  if (!resp.ok) throw new Error(resp.statusText || 'request failed');
}

/**
 * Dev-sync J5 — the sidebar SYNCHRONIZE POST (synchronizeRow js:1427-1449):
 * POSTs the freshly-propagated conditions straight onto the model via
 * commit_air_conditions (the only verb wired for the UDA route; the section
 * route is what fires the Checkpoint-4 cascade when sync is on). Rejects
 * with the legacy error-message resolution (responseJSON.detail ||
 * responseText || statusText — js:1445-1447) so the caller can show
 * "Could not synchronize: <msg>".
 */
export async function postCommitAirConditions(
  url: string,
  payload: Record<string, unknown>
): Promise<void> {
  const resp = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    let msg = '';
    try {
      const body = await resp.text();
      try {
        const parsed = JSON.parse(body) as { detail?: string };
        msg = parsed.detail || body;
      } catch {
        msg = body;
      }
    } catch {
      /* unreadable body */
    }
    throw new Error(msg || resp.statusText || 'request failed');
  }
}

/**
 * Dev-sync J4 — the #psy-sync-toggle PATCH (onSyncToggle js:607-630): the
 * ONLY place in the whole UI that flips AHUUnit.synchronize_air_conditions
 * (survey §A2 — which makes this popup a hard dependency for the entire
 * cascade feature). Legacy used its own raw $.ajax, NOT the board's Backbone
 * model (see the popup for the cache-coherence note). Rejects on failure so
 * the caller can show the legacy status message. Note the unit URL has NO
 * trailing slash (js:612).
 */
export async function patchSynchronizeAirConditions(ahuId: number, on: boolean): Promise<void> {
  const resp = await fetch(`/api/v1.0/ahu/units/${ahuId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify({ synchronize_air_conditions: on }),
  });
  if (!resp.ok) throw new Error(resp.statusText || 'request failed');
}
